import { Columns3, Plus, Rows3, Trash2, type LucideIcon } from 'lucide-react'
import { useState, type FocusEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { confirmModal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import {
  addColumn,
  addRow,
  columnCount,
  columnHasContent,
  removeColumn,
  removeRow,
  rowHasContent,
  setCell,
  TABLE_MAX_COLUMNS,
  type TableRows,
} from '../lib/tableOps'
import { TABLE_TONE_KEYS, TABLE_TONES } from '../lib/tableTones'
import type { TableTone } from '../types'

type Props = {
  rows: TableRows
  tone?: TableTone
  editable: boolean
  onChange: (rows: TableRows) => void
  onToneChange?: (tone: TableTone) => void
}
type Cell = { r: number; c: number }

export function TableBlock({ rows, tone = 'primary', editable, onChange, onToneChange }: Props) {
  const { t } = useTranslation()
  const [active, setActive] = useState<Cell | null>(null)
  const [header = [], ...body] = rows
  const cols = columnCount(rows)
  const style = TABLE_TONES[tone]

  function onBlur(e: FocusEvent<HTMLDivElement>) {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActive(null)
  }

  function handleAddColumn() {
    if (cols >= TABLE_MAX_COLUMNS) {
      toast.info(t('hr.belgeler.table.maxColumns', { n: TABLE_MAX_COLUMNS }))
      return
    }
    onChange(addColumn(rows, t('hr.belgeler.table.column', { n: cols + 1 }), active?.c))
  }

  async function handleRemoveRow() {
    if (!active || active.r === 0) return
    if (rowHasContent(rows, active.r)) {
      const ok = await confirmModal({
        title: t('hr.belgeler.table.removeRowTitle'),
        description: t('hr.belgeler.table.removeRowBody'),
        confirmLabel: t('hr.belgeler.table.removeRow'),
        destructive: true,
      })
      if (!ok) return
    }
    onChange(removeRow(rows, active.r))
    setActive(null)
  }

  async function handleRemoveColumn() {
    if (!active) return
    if (columnHasContent(rows, active.c)) {
      const ok = await confirmModal({
        title: t('hr.belgeler.table.removeColumnTitle'),
        description: t('hr.belgeler.table.removeColumnBody', { name: header[active.c] || t('hr.belgeler.table.column', { n: active.c + 1 }) }),
        confirmLabel: t('hr.belgeler.table.removeColumn'),
        destructive: true,
      })
      if (!ok) return
    }
    onChange(removeColumn(rows, active.c))
    setActive(null)
  }

  function cellInput(r: number, c: number, value: string, head: boolean) {
    if (!editable) return <span className="block min-h-[1.5em] whitespace-pre-wrap break-words px-2.5 py-1.5">{value}</span>
    return (
      <input
        value={value}
        aria-label={head ? t('hr.belgeler.table.headerLabel', { col: c + 1 }) : t('hr.belgeler.table.cellLabel', { row: r, col: c + 1 })}
        placeholder={head ? t('hr.belgeler.table.headerPlaceholder') : t('hr.belgeler.table.cellPlaceholder')}
        onFocus={() => setActive({ r, c })}
        onChange={(e) => onChange(setCell(rows, r, c, e.target.value))}
        className={cn(
          'block w-full min-w-0 bg-transparent px-2.5 py-1.5 outline-none transition-colors',
          head ? cn('font-semibold', style.input) : 'text-foreground placeholder:text-muted-foreground/50 focus:bg-primary/[0.06]',
        )}
      />
    )
  }

  return (
    <div className="group/table relative" onClick={(e) => e.stopPropagation()} onBlur={onBlur}>
      {editable ? (
        <div
          role="toolbar"
          aria-label={t('hr.belgeler.table.label')}
          className="absolute bottom-full right-0 z-10 mb-1 hidden items-center gap-0.5 rounded-lg border bg-card p-0.5 shadow-[0_6px_16px_-6px_color-mix(in_oklab,var(--shadow-color)_30%,transparent)] group-focus-within/table:flex group-hover/table:flex"
        >
          <ToolBtn icon={Rows3} label={t('hr.belgeler.table.addRow')} onClick={() => onChange(addRow(rows, active?.r))} plus />
          <ToolBtn icon={Columns3} label={t('hr.belgeler.table.addColumn')} onClick={handleAddColumn} plus />
          <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
          <ToolBtn icon={Rows3} label={t('hr.belgeler.table.removeRow')} onClick={() => void handleRemoveRow()} disabled={!active || active.r === 0 || body.length <= 1} danger />
          <ToolBtn icon={Columns3} label={t('hr.belgeler.table.removeColumn')} onClick={() => void handleRemoveColumn()} disabled={!active || cols <= 1} danger />
          {onToneChange ? (
            <>
              <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
              <div role="radiogroup" aria-label={t('hr.belgeler.table.headerColor')} className="flex items-center gap-1 px-1">
                {TABLE_TONE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={tone === key}
                    aria-label={t(`hr.belgeler.table.tones.${key}`)}
                    title={t(`hr.belgeler.table.tones.${key}`)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onToneChange(key)}
                    className={cn(
                      'size-4 rounded-full transition-transform hover:scale-110',
                      TABLE_TONES[key].swatch,
                      tone === key && 'ring-2 ring-primary/60 ring-offset-2 ring-offset-card',
                    )}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-md border border-border shadow-[0_1px_2px_color-mix(in_oklab,var(--shadow-color)_6%,transparent)]">
        <table className="w-full table-fixed border-collapse text-[0.95em]">
          <thead>
            <tr className={cn('text-left', style.header)}>
              {header.map((cell, c) => (
                <th key={c} scope="col" className={cn('border-r p-0 align-middle font-semibold last:border-r-0', style.divider)}>
                  {cellInput(0, c, cell, true)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, i) => (
              <tr key={i} className={cn('border-t border-border/70', i % 2 === 1 && 'bg-muted/45', active?.r === i + 1 && 'bg-primary/[0.06]')}>
                {row.map((cell, c) => (
                  <td key={c} className="border-r border-border/60 p-0 align-top last:border-r-0">
                    {cellInput(i + 1, c, cell, false)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ToolBtn({
  icon: Icon,
  label,
  onClick,
  disabled,
  plus,
  danger,
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
  disabled?: boolean
  plus?: boolean
  danger?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex h-6 items-center gap-0.5 rounded-md px-1.5 text-muted-foreground transition-colors disabled:pointer-events-none disabled:opacity-35',
        danger ? 'hover:bg-destructive/10 hover:text-destructive' : 'hover:bg-primary/10 hover:text-primary',
      )}
    >
      {plus ? <Plus className="size-3" /> : <Trash2 className="size-3" />}
      <Icon className="size-3.5" />
    </button>
  )
}
